import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, ButtonBase, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { getSVG } from '../../utils/getSvg'

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent || '')

/** The admin shell's search palette (resto-app-admin components/globalSearch/events.js). */
const OPEN_EVENT = 'app:global-search-open'
const READY_EVENT = 'app:global-search-ready'

/** Whether the admin shell's search palette is there to open (it is not when this app runs on its own). */
const useShellSearch = () => {
  const [ready, setReady] = useState(() => typeof window !== 'undefined' && Boolean(window.__APP_GLOBAL_SEARCH__))
  useEffect(() => {
    const onReady = () => setReady(true)
    window.addEventListener(READY_EVENT, onReady)
    return () => window.removeEventListener(READY_EVENT, onReady)
  }, [])
  return ready
}

const MAX_WIDTH = 460
/** Narrowest the box gets to stay centred; with less room it fills the space it has instead. */
const MIN_CENTRED_WIDTH = 240

/**
 * Centres the box on the header row rather than on the room left between the context control and
 * the buttons (they differ in width, so that room is off-centre). Returns the box's width and how far
 * to shift it, or null when it can't be centred at a usable width (or the slot is hidden).
 */
const useCentredOnRow = (slotRef, enabled) => {
  const [place, setPlace] = useState(null)
  useLayoutEffect(() => {
    const slot = slotRef.current
    const row = slot?.parentElement
    if (!enabled || !slot || !row || typeof ResizeObserver === 'undefined') return undefined
    const measure = () => {
      const s = slot.getBoundingClientRect()
      const r = row.getBoundingClientRect()
      const style = window.getComputedStyle(slot)
      const left = s.left + (parseFloat(style.paddingLeft) || 0)
      const right = s.right - (parseFloat(style.paddingRight) || 0)
      const centre = r.left + r.width / 2
      const width = Math.floor(Math.min(MAX_WIDTH, 2 * Math.min(centre - left, right - centre)))
      const next = s.width > 0 && width >= MIN_CENTRED_WIDTH
        ? { width, shift: Math.round(centre - (left + right) / 2) }
        : null
      setPlace((prev) => (prev?.width === next?.width && prev?.shift === next?.shift ? prev : next))
    }
    measure()
    // The slot resizes when either neighbour does; the row when the window or the sidebar does.
    const observer = new ResizeObserver(measure)
    observer.observe(slot)
    observer.observe(row)
    return () => observer.disconnect()
  }, [slotRef, enabled])
  return place
}

/**
 * The header's search box, centred on the header row (see useCentredOnRow): opens the admin shell's
 * search palette (pages and records across the admin), as Ctrl/⌘ K does from anywhere. Shown only
 * inside the shell; below `md`, where the header has no room for the box, a search button beside
 * the bell (the shell's own header does the same).
 */
const HeaderSearch = ({ placeholder }) => {
  const theme = useTheme()
  const ready = useShellSearch()
  const slotRef = useRef(null)
  const compact = useMediaQuery(theme.breakpoints.down('md'))
  const place = useCentredOnRow(slotRef, ready && !compact)
  if (!ready) return null
  const open = () => window.dispatchEvent(new CustomEvent(OPEN_EVENT))
  if (compact) {
    return (
      // Takes the header's free room and sits at its end, so the button is beside the bell. Never
      // narrower than the button: squeezed, it spilled left over the restaurant / branch control.
      <Box sx={{ flex: '1 0 auto', display: 'flex', justifyContent: 'flex-end', pl: 0.5 }}>
        <Tooltip title={placeholder}>
          <IconButton
            onClick={open}
            aria-haspopup="dialog"
            aria-label={placeholder}
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.06) },
            }}
          >
            {getSVG('lucide-search', theme.palette.text.secondary, { width: 20, height: 20 }, undefined, undefined, true)}
          </IconButton>
        </Tooltip>
      </Box>
    )
  }
  return (
    <Box ref={slotRef} sx={{ flex: '1 1 0%', minWidth: 0, display: 'flex', justifyContent: 'center', px: 2 }}>
      <ButtonBase
        onClick={open}
        aria-haspopup="dialog"
        aria-label={placeholder}
        sx={{
          width: place ? place.width : '100%',
          maxWidth: MAX_WIDTH,
          transform: place?.shift ? `translateX(${place.shift}px)` : 'none',
          height: 40,
          gap: 1.25,
          px: 1.5,
          justifyContent: 'flex-start',
          borderRadius: '12px',
          border: 1,
          borderColor: 'divider',
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.secondary,
          transition: theme.transitions.create(['border-color', 'box-shadow'], { duration: theme.transitions.duration.shorter }),
          '&:hover': { borderColor: alpha(theme.palette.primary.main, 0.5) },
          '&.Mui-focusVisible': { borderColor: theme.palette.primary.main, boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}` },
        }}
      >
        {getSVG('lucide-search', theme.palette.text.secondary, { width: 17, height: 17 }, undefined, undefined, true)}
        <Typography noWrap sx={{ flex: 1, minWidth: 0, textAlign: 'start', fontSize: 14, color: 'inherit' }}>
          {placeholder}
        </Typography>
        <Box
          component="kbd"
          sx={{
            px: 1,
            py: '2px',
            borderRadius: '6px',
            border: 1,
            borderColor: 'divider',
            fontFamily: 'inherit',
            fontSize: 12,
            lineHeight: 1.5,
            whiteSpace: 'nowrap',
          }}
        >
          {isMac ? '⌘ K' : 'Ctrl K'}
        </Box>
      </ButtonBase>
    </Box>
  )
}

HeaderSearch.propTypes = {
  placeholder: PropTypes.string,
}

export default HeaderSearch
